import { Temporal } from "@js-temporal/polyfill";
import { z } from "zod";
import { normalizePhone } from "./phone";
export const brand = {
  name: "Aethon Ink",
  tagline: "A little order. More room to create.",
};
export const settingsSchema = z.object({
  theme: z.enum(["dark", "light", "system"]),
  timeFormat: z.enum(["12", "24"]),
  defaultDuration: z.number().int().min(1).max(10080),
  timezoneConfigured: z.boolean().default(false),
  timezone: z.string().refine((v) => {
    try {
      Temporal.Now.zonedDateTimeISO(v);
      return true;
    } catch {
      return false;
    }
  }, "Choose a valid IANA timezone"),
  notifications: z.boolean(),
  reminderTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
});
export type Settings = z.infer<typeof settingsSchema>;
export const clientSchema = z.object({
  name: z.string().trim().min(1).max(120),
  phone: z
    .string()
    .trim()
    .max(40)
    .refine(
      (value) => normalizePhone(value) !== null,
      "Enter a valid Lebanese number or an international number starting with +",
    )
    .transform((value) => normalizePhone(value)!),
});
export const MAX_NOTES_LENGTH = 5000;
export const appointmentSchema = z
  .object({
    clientId: z.string().optional(),
    notes: z
      .string()
      .max(MAX_NOTES_LENGTH, "Notes must be 5,000 characters or fewer")
      .optional(),
    name: clientSchema.shape.name,
    phone: z.string().trim().max(40),
    start: z.number().int().min(0).max(8640000000000000),
    duration: z.number().int().min(1).max(10080),
  })
  .superRefine((value, ctx) => {
    if (!value.clientId && normalizePhone(value.phone) === null)
      ctx.addIssue({
        code: "custom",
        path: ["phone"],
        message:
          "Enter a valid Lebanese number or an international number starting with +",
      });
  })
  .transform((value) => ({
    ...value,
    phone: value.clientId ? value.phone : normalizePhone(value.phone)!,
  }));
export type AppointmentInput = z.infer<typeof appointmentSchema>;
export interface Client {
  id: string;
  name: string;
  phone: string;
  created_at: number;
}
export interface Appointment {
  id: string;
  client_id: string;
  name: string;
  phone: string;
  start: number;
  duration: number;
  created_at: number;
  deleted_at: number | null;
  notes?: string | null;
}
export interface Snapshot {
  clients: Client[];
  appointments: Appointment[];
  settings: Settings;
  expiresAt: number;
  username: string;
}
export const defaults: Settings = {
  theme: "dark",
  timeFormat: "24",
  defaultDuration: 60,
  timezone: "Asia/Beirut",
  timezoneConfigured: false,
  notifications: false,
  reminderTime: "23:00",
};
export function overlaps(
  a: { start: number; duration: number },
  b: { start: number; duration: number },
) {
  return (
    a.start < b.start + b.duration * 60000 &&
    a.start + a.duration * 60000 > b.start
  );
}
export function durationLabel(m: number) {
  return [
    Math.floor(m / 60) ? `${Math.floor(m / 60)}h` : "",
    m % 60 ? `${m % 60}m` : "",
  ]
    .filter(Boolean)
    .join(" ");
}
export function dateKey(ms: number, tz: string) {
  return Temporal.Instant.fromEpochMilliseconds(ms)
    .toZonedDateTimeISO(tz)
    .toPlainDate()
    .toString();
}
export function addDays(date: string, days: number) {
  return Temporal.PlainDate.from(date).add({ days }).toString();
}
export function localStart(date: string, time: string, tz: string) {
  return Temporal.PlainDateTime.from(`${date}T${time}`).toZonedDateTime(tz, {
    disambiguation: "reject",
  }).epochMilliseconds;
}
export function localTime(ms: number, tz: string) {
  return Temporal.Instant.fromEpochMilliseconds(ms)
    .toZonedDateTimeISO(tz)
    .toPlainTime()
    .toString()
    .slice(0, 5);
}
export function formatTime(ms: number, s: Settings) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: s.timezone,
    hour: "numeric",
    minute: "2-digit",
    hour12: s.timeFormat === "12",
  }).format(ms);
}
export function dateLabel(
  date: string,
  options: Intl.DateTimeFormatOptions = {
    weekday: "long",
    month: "short",
    day: "numeric",
  },
) {
  return new Intl.DateTimeFormat("en-US", {
    ...options,
    timeZone: "UTC",
  }).format(new Date(date + "T12:00:00Z"));
}
export function reminderTarget(now: number, s: Settings) {
  const day = dateKey(now, s.timezone);
  return Number(s.reminderTime.slice(0, 2)) < 12 ? day : addDays(day, 1);
}
export function nextReminder(now: number, s: Settings) {
  const zoned = Temporal.Instant.fromEpochMilliseconds(now).toZonedDateTimeISO(
    s.timezone,
  );
  let date = zoned.toPlainDate();
  for (let i = 0; i < 3; i++) {
    const next = date
      .toPlainDateTime(s.reminderTime)
      .toZonedDateTime(s.timezone, {
        disambiguation: "compatible",
      }).epochMilliseconds;
    if (next > now) return next;
    date = date.add({ days: 1 });
  }
  throw new Error("Unable to schedule reminder");
}
export function summary(now: number, s: Settings, appointments: Appointment[]) {
  const target = reminderTarget(now, s);
  const items = appointments
    .filter((a) => !a.deleted_at && dateKey(a.start, s.timezone) === target)
    .sort((a, b) => a.start - b.start);
  if (!items.length) return null;
  const word = Number(s.reminderTime.slice(0, 2)) < 12 ? "Today" : "Tomorrow";
  return {
    title: `${brand.name} — ${word}`,
    body:
      `You have ${items.length} appointment${items.length === 1 ? "" : "s"} ${word.toLowerCase()}.\n` +
      items.map((a) => `${formatTime(a.start, s)} — ${a.name}`).join("\n"),
    tag: `summary-${target}`,
    url: "/schedule",
  };
}

// Match the weekday strip: one marker per appointment start date, including past dates.
export function appointmentDates(
  appointments: Appointment[],
  timezone: string,
) {
  return new Set(
    appointments
      .filter((a) => !a.deleted_at)
      .map((a) => dateKey(a.start, timezone)),
  );
}
